import {
  EnhancedSeoHead,
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateFAQSchema,
  NotFoundPage,
} from "@appcaire/shared/seo";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Info,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { ConversionCtaBand } from "../components/ConversionCtaBand";
import { useJourney } from "../contexts/JourneyContext";
import { useStockholmEventTracking } from "../lib/analytics";
import { fitTitle } from "../lib/seo-text";
import {
  OFFICIAL_STOCKHOLM_CHOOSER_URL,
  OFFICIAL_STOCKHOLM_FEES_URL,
  OFFICIAL_STOCKHOLM_HOME_CARE_URL,
  SITE_ORIGIN,
  STOCKHOLM_GUIDES,
} from "../lib/site";

interface GuideDefinition {
  title: string;
  description: string;
  eyebrow: string;
  keywords: string[];
  intro: string;
  steps: Array<{ title: string; body: string }>;
  questions: string[];
  officialLabel: string;
  officialUrl: string;
  nextLabel: string;
  nextPath: string;
  datePublished: string;
  dateModified: string;
  faqs?: Array<{ question: string; answer: string }>;
}

const GUIDES: Record<string, GuideDefinition> = {
  "nya-socialtjanstlagen-hemtjanst-stockholm": {
    title: "Nya socialtjänstlagen och hemtjänsten i Stockholm",
    description:
      "Vad betyder den nya socialtjänstlagen för dig som söker hemtjänst i Stockholm? Läs om ansökan, förebyggande stöd och kommunens ansvar.",
    eyebrow: "Ny lag, praktisk vägledning",
    keywords: [
      "nya socialtjänstlagen Stockholm",
      "nya socialtjänstlagen hemtjänst",
      "hemtjänst Stockholm lag",
    ],
    intro:
      "Den nya socialtjänstlagen ska göra socialtjänsten mer förebyggande och lättare att nå. Här förklarar vi vad det betyder i Stockholm och vad du fortfarande behöver ansöka om.",
    steps: [
      {
        title: "Lagen gäller i hela Sverige",
        body: "Den nya socialtjänstlagen (SFS 2025:400) gäller sedan 1 juli 2025. Stockholm följer lagen, men Stockholms stad bestämmer hur lokala kontaktvägar och stödinsatser organiseras.",
      },
      {
        title: "Du ansöker fortfarande om behovsprövad hemtjänst",
        body: "När du behöver hemtjänst utifrån dina individuella behov kontaktar du Stockholms stad. En biståndshandläggare utreder behovet och fattar beslut.",
      },
      {
        title: "Förebyggande stöd ska vara lättare att nå",
        body: "Kommunens förebyggande arbete kan handla om information, tidiga stödinsatser och enklare vägar in. Fråga Stockholms stad vilka öppna eller förebyggande insatser som finns just nu.",
      },
      {
        title: "Vissa insatser kan erbjudas utan behovsprövning",
        body: "Lagen gör det möjligt för kommunen att erbjuda vissa personliga insatser utan individuell behovsprövning. Det är en möjlighet, inte en automatisk rätt till hemtjänst, och utbudet kan ändras.",
      },
      {
        title: "Välj utförare efter beslut",
        body: "När du har fått ett beslut och har rätt att välja utförare kan du jämföra alternativ i Stockholm. Fråga särskilt om kontinuitet, kontaktvägar och hur starten planeras.",
      },
    ],
    questions: [
      "Vilka förebyggande stöd finns i Stockholm?",
      "Hur ansöker jag om behovsprövad hemtjänst?",
      "Kan någon insats erbjudas utan individuell behovsprövning?",
      "Hur väljer eller byter jag utförare?",
    ],
    officialLabel: "Läs om hemtjänst hos Stockholms stad",
    officialUrl: OFFICIAL_STOCKHOLM_HOME_CARE_URL,
    nextLabel: "Ansök om hemtjänst",
    nextPath: "/guider/ansoka-hemtjanst-stockholm",
    datePublished: "2026-08-05",
    dateModified: "2026-08-05",
    faqs: [
      {
        question: "När började den nya socialtjänstlagen gälla?",
        answer:
          "Den nya socialtjänstlagen (SFS 2025:400) trädde i kraft den 1 juli 2025.",
      },
      {
        question: "Får jag automatiskt hemtjänst enligt den nya lagen?",
        answer:
          "Nej. Behovsprövad hemtjänst kräver fortfarande en ansökan och individuell bedömning av Stockholms stad.",
      },
      {
        question: "Var hittar jag aktuell information om Stockholm?",
        answer:
          "Kontrollera alltid Stockholms stads officiella information för aktuella kontaktvägar, regler och lokala stödinsatser.",
      },
    ],
  },
  "sa-fungerar-hemtjanst": {
    title: "Så fungerar hemtjänst i Stockholm",
    description:
      "Vad hemtjänst är, vilken hjälp du kan få, vad det kostar och hur du väljer utförare i Stockholm.",
    eyebrow: "Börja här",
    keywords: [
      "vad är hemtjänst",
      "hur fungerar hemtjänst Stockholm",
      "hemtjänst Stockholm",
    ],
    intro:
      "Hemtjänst är hjälp hemma så att du kan bo kvar och känna dig trygg i vardagen. Det kan vara allt från lite hjälp några timmar i månaden till stöd flera gånger om dagen. Så här går det till i Stockholm.",
    steps: [
      {
        title: "Vad är hemtjänst?",
        body: "Hemtjänst är stöd i ditt eget hem som du får efter ett beslut från Stockholms stad. Hjälpen anpassas efter dina behov och kan ändras om behoven förändras.",
      },
      {
        title: "Service och personlig omvårdnad",
        body: "Serviceinsatser är praktisk hjälp som städning, tvätt, inköp och måltider. Personlig omvårdnad är hjälp nära kroppen, till exempel dusch, att sköta toalettbestyr och att klä på och av sig.",
      },
      {
        title: "Så ansöker du – fyra steg",
        body: "Först ansöker du hos Stockholms stad. Sedan träffar du en biståndshandläggare som utreder dina behov. Därefter får du ett beslut om vilken hjälp du beviljas. Till sist väljer du vilken utförare som ska hjälpa dig.",
      },
      {
        title: "Vad det kostar",
        body: "Du betalar en avgift som beror på din inkomst, och du betalar aldrig mer än maxtaxan – 2 660 kronor i månaden 2026. Har du låg inkomst eller höga boendekostnader kan avgiften bli lägre, ibland 0 kronor.",
      },
      {
        title: "Du väljer utförare själv",
        body: "I Stockholm får du välja mellan kommunala och privata utförare. Väljer du ingen får du en utförare anvisad. Du har alltid rätt att byta senare om du inte trivs.",
      },
    ],
    questions: [
      "Vilken hjälp behöver jag i vardagen?",
      "Vill jag ha en kommunal eller privat utförare?",
      "Vilka språk är viktiga för mig?",
      "Vem kan hjälpa mig att ansöka?",
    ],
    officialLabel: "Läs om hemtjänst hos Stockholms stad",
    officialUrl: OFFICIAL_STOCKHOLM_HOME_CARE_URL,
    nextLabel: "Hitta och jämför utförare",
    nextPath: "/hitta-hemtjanst",
    datePublished: "2026-07-12",
    dateModified: "2026-07-12",
    faqs: [
      {
        question: "Vad är hemtjänst?",
        answer:
          "Hemtjänst är hjälp i ditt eget hem så att du kan bo kvar och känna dig trygg. Det kan vara praktisk hjälp som städning, tvätt och inköp, eller personlig omvårdnad som dusch och påklädning. Du får hemtjänst efter ett beslut från Stockholms stad.",
      },
      {
        question: "Vad kostar hemtjänst i Stockholm?",
        answer:
          "Avgiften beror på din inkomst. Du betalar aldrig mer än maxtaxan, som är 2 660 kronor i månaden 2026. Med låg inkomst eller höga boendekostnader kan avgiften bli lägre – ibland 0 kronor.",
      },
      {
        question: "Hur ansöker jag om hemtjänst?",
        answer:
          "Du ansöker hos Stockholms stad. En biståndshandläggare utreder dina behov och du får ett beslut om vilken hjälp du beviljas. Därefter väljer du själv vilken utförare som ska hjälpa dig.",
      },
      {
        question: "Måste jag välja utförare själv?",
        answer:
          "Nej. I Stockholm får du välja mellan kommunala och privata utförare, men om du inte vill välja anvisas du en utförare. Du kan alltid byta senare.",
      },
      {
        question: "Kan jag byta hemtjänstutförare?",
        answer:
          "Ja. Du har rätt att byta utförare om du inte är nöjd. Byten sköts genom Stockholms stad, och du kan jämföra nya utförare här innan du bestämmer dig.",
      },
    ],
  },
  "ansoka-hemtjanst-stockholm": {
    title: "Ansöka om hemtjänst i Stockholm",
    description:
      "Ansök om hemtjänst via Stockholms stad. En biståndshandläggare utreder behoven – därefter väljer du utförare själv.",
    eyebrow: "Från behov till beslut",
    keywords: [
      "ansöka hemtjänst Stockholm",
      "biståndsbedömare Stockholm",
      "hemtjänst ansökan",
    ],
    intro:
      "Hemtjänst börjar med ett beslut från Stockholms stad. Guiden hjälper dig att förbereda ansökan, beskriva behoven konkret och förstå när det är dags att välja utförare.",
    steps: [
      {
        title: "Beskriv vardagen, inte bara diagnoser",
        body: "Skriv ned vilka moment som inte fungerar tryggt i dag: till exempel personlig omvårdnad, måltider, inköp eller städning. Notera när på dygnet hjälpen behövs och vad du fortfarande gör själv.",
      },
      {
        title: "Kontakta Stockholms stad",
        body: "Ansökan och kontakt med biståndshandläggare sker genom Stockholms stads officiella kanaler. Kommunen utreder behovet och fattar beslut om vilka insatser som beviljas.",
      },
      {
        title: "Läs beslutet noggrant",
        body: "Kontrollera vilka insatser, vilken omfattning och vilka villkor som står i beslutet. Be handläggaren förklara sådant som är oklart och hur du går vidare om du inte håller med.",
      },
      {
        title: "Välj utförare",
        body: "När beslutet ger rätt att välja kan du använda vår lista som stöd. Ring och bekräfta att utföraren kan börja hemma hos dig och möta de behov som står i beslutet.",
      },
      {
        title: "Följ upp starten",
        body: "Kom överens om kontaktväg, tider, fast omsorgskontakt och hur genomförandeplanen tas fram. Spara namn och telefonnummer så att du vet vart du vänder dig vid frågor.",
      },
    ],
    questions: [
      "Vilka vardagsmoment behöver stöd?",
      "När på dygnet behövs hjälpen?",
      "Vem kan vara med vid mötet?",
      "Hur får jag beslutet och hur överklagar jag?",
    ],
    officialLabel: "Ansök om hemtjänst hos Stockholms stad",
    officialUrl: OFFICIAL_STOCKHOLM_HOME_CARE_URL,
    nextLabel: "Hitta utförare",
    nextPath: "/hitta-hemtjanst",
    datePublished: "2026-07-12",
    dateModified: "2026-07-25",
    faqs: [
      {
        question: "Hur ansöker jag om hemtjänst i Stockholm?",
        answer:
          "Du ansöker hos Stockholms stad. En biståndshandläggare utreder dina behov och du får ett skriftligt beslut om vilken hjälp du beviljas. Sedan väljer du själv utförare.",
      },
      {
        question: "Vad gör jag om jag inte håller med om beslutet?",
        answer:
          "Du har rätt att överklaga beslutet. Be din biståndshandläggare förklara hur du gör och inom vilken tid, så att din överklagan hinner fram.",
      },
    ],
  },
  "kostnad-hemtjanst-stockholm": {
    title: "Vad kostar hemtjänst i Stockholm?",
    description:
      "Förstå vad som påverkar avgiften för hemtjänst i Stockholm och vilka uppgifter kommunen behöver.",
    eyebrow: "Avgift och underlag",
    keywords: [
      "kostnad hemtjänst Stockholm",
      "avgift hemtjänst Stockholm",
      "maxtaxa hemtjänst",
    ],
    intro:
      "Det är Stockholms stad som beslutar om avgiften utifrån dina inkomster och aktuella regler – inte utföraren. Du betalar aldrig mer än maxtaxan, och avgiften kan bli 0 kronor om din inkomst är låg.",
    steps: [
      {
        title: "Så mycket kan du betala",
        body: "Avgiften är inkomstprövad och har ett tak: maxtaxan är 2 660 kronor i månaden 2026, oavsett hur mycket hjälp du har. Har du låg pension eller hög hyra kan avgiften bli lägre – ibland 0 kronor. Insatser i ditt beslut avgiftssätts av kommunen; fråga separat om en utförare erbjuder tilläggstjänster utanför beslutet.",
      },
      {
        title: "Lämna efterfrågade inkomstuppgifter",
        body: "Kommunen behöver ett korrekt underlag för att räkna fram avgiftsutrymmet. Kontrollera vilka inkomster, boendekostnader och hushållsuppgifter som ska lämnas i det aktuella ärendet.",
      },
      {
        title: "Kontrollera avgiftsbeslutet",
        body: "Läs vilket underlag kommunen har använt och från vilket datum avgiften gäller. Kontakta avgiftshandläggningen om uppgifterna är fel eller om ekonomin förändras.",
      },
      {
        title: "Jämför kvalitet, inte kommunavgiften",
        body: "Avgiften är samma oavsett vilken utförare du väljer, så fokusera jämförelsen på det som skiljer dem åt: bemötande, kontinuitet, språk, stadsdel och hur de arbetar.",
      },
    ],
    questions: [
      "Vilka underlag behöver kommunen?",
      "När börjar avgiften gälla?",
      "Ingår trygghetslarm eller mat i samma beslut?",
      "Finns privata tillägg och är de frivilliga?",
    ],
    officialLabel: "Se aktuella avgifter hos Stockholms stad",
    officialUrl: OFFICIAL_STOCKHOLM_FEES_URL,
    nextLabel: "Så väljer du utförare",
    nextPath: "/guider/valja-hemtjanst",
    datePublished: "2026-07-12",
    dateModified: "2026-07-12",
    faqs: [
      {
        question: "Vad kostar hemtjänst i Stockholm 2026?",
        answer:
          "Avgiften är inkomstprövad och du betalar aldrig mer än maxtaxan, 2 660 kronor i månaden 2026. Det är Stockholms stad som räknar ut din avgift utifrån dina inkomster och boendekostnader.",
      },
      {
        question: "Kan avgiften bli 0 kronor?",
        answer:
          "Ja. Om din inkomst är låg i förhållande till dina boendekostnader kan avgiftsutrymmet bli så litet att du inte betalar något alls. Staden gör alltid en individuell beräkning.",
      },
    ],
  },
  "valja-hemtjanst": {
    title: "Välja hemtjänstutförare i Stockholm",
    description:
      "Efter beslut väljer du utförare själv i Stockholm. Jämför stadsdel, kontinuitet och språk innan du bestämmer dig.",
    eyebrow: "Ett lugnare val",
    keywords: [
      "välja hemtjänst Stockholm",
      "jämföra hemtjänstutförare",
      "bästa hemtjänst Stockholm",
    ],
    intro:
      "Det finns sällan en enda utförare som är bäst för alla. Börja med behoven i ditt beslut och välj sedan ut några få utförare att titta närmare på.",
    steps: [
      {
        title: "Kontrollera stadsdel och att de kan börja",
        body: "Vi visar vilka stadsdelar utföraren arbetar i enligt Stockholms stad. Ring och bekräfta att de kan börja hemma hos dig och vid de tider du behöver.",
      },
      {
        title: "Fråga om kontinuitet",
        body: "Personalkontinuitet visar hur många olika personer som hjälper dig under två veckor – färre är bättre. Saknas siffran säger det inget om kvaliteten, så vi visar den aldrig som noll.",
      },
      {
        title: "Pröva språk och kompetens mot dina behov",
        body: "Bekräfta vem som faktiskt kan tala önskat språk och vilken erfarenhet teamet har. En profiluppgift är ett underlag för frågor, inte en garanti för varje besök.",
      },
      {
        title: "Be om konkret arbetssätt",
        body: "Fråga hur planering, förseningar, kontakt med anhöriga och byte av personal hanteras. Be om ett tydligt telefonnummer för vardagliga frågor och avvikelser.",
      },
      {
        title: "Spara högst tre",
        body: "En kortlista med två eller tre alternativ gör skillnaderna lättare att se. Dela eller skriv ut jämförelsen inför samtal med närstående eller biståndshandläggare.",
      },
    ],
    questions: [
      "Kan ni starta på min adress och när?",
      "Hur arbetar ni med fast omsorgskontakt?",
      "Hur många olika personer brukar komma?",
      "Hur når jag er vid försening eller förändrat behov?",
    ],
    officialLabel: "Öppna Stockholms officiella utförarval",
    officialUrl: OFFICIAL_STOCKHOLM_CHOOSER_URL,
    nextLabel: "Börja jämföra",
    nextPath: "/hitta-hemtjanst",
    datePublished: "2026-07-12",
    dateModified: "2026-07-25",
    faqs: [
      {
        question: "Vilken är den bästa hemtjänsten i Stockholm?",
        answer:
          "Det finns ingen utförare som är bäst för alla. Den bästa för dig beror på var du bor, vilka språk du vill tala, vad som är viktigast för dig och hur utföraren arbetar. Jämför några få och fråga om det som betyder mest.",
      },
      {
        question: "Hur många utförare bör jag jämföra?",
        answer:
          "Två eller tre räcker oftast. En kort lista gör skillnaderna lättare att se. Du kan spara upp till tre utförare och jämföra dem sida vid sida här.",
      },
    ],
  },
  "byta-hemtjanst": {
    title: "Byta hemtjänst i Stockholm",
    description:
      "Du har rätt att byta hemtjänstutförare i Stockholm. Gör bytet via staden och jämför nya alternativ först.",
    eyebrow: "När nuvarande stöd inte fungerar",
    keywords: [
      "byta hemtjänst Stockholm",
      "byta hemtjänstutförare",
      "missnöjd hemtjänst Stockholm",
    ],
    intro:
      "Ett byte blir tryggare om du först skiljer på akuta problem, synpunkter som kan rättas till och ett långsiktigt behov av en annan utförare.",
    steps: [
      {
        title: "Skriv ned vad som behöver förändras",
        body: "Notera konkreta händelser, datum och vilken del av överenskommelsen som inte fungerar. Vid akut fara ska du använda rätt akut kontaktväg, inte vänta på ett vanligt bytesärende.",
      },
      {
        title: "Ta upp problemet med nuvarande utförare",
        body: "Be om besked om vad som kan ändras, vem som ansvarar och när ni följer upp. Om problemet gäller beslutets innehåll kan biståndshandläggaren behöva kopplas in.",
      },
      {
        title: "Kontrollera bytesprocessen hos staden",
        body: "Stockholms stad ansvarar för reglerna kring val och byte. Bekräfta hur du meddelar valet, vilket startdatum som är möjligt och om något behöver överlämnas.",
      },
      {
        title: "Jämför nya alternativ",
        body: "Välj utförare som arbetar i din stadsdel och fråga särskilt om det som inte fungerat tidigare. Be om en tydlig plan för start, kontakt och genomförandeplan.",
      },
      {
        title: "Planera överlämningen",
        body: "Säkerställ att viktiga kontaktuppgifter och beslut följer rätt process. Dela bara personuppgifter genom kommunens och utförarens avsedda säkra kanaler.",
      },
    ],
    questions: [
      "Vad vill jag konkret ska bli annorlunda?",
      "Vem ansvarar under övergången?",
      "När kan den nya utföraren starta?",
      "Hur följs de första veckorna upp?",
    ],
    officialLabel: "Läs om hemtjänst hos Stockholms stad",
    officialUrl: OFFICIAL_STOCKHOLM_HOME_CARE_URL,
    nextLabel: "Hitta en ny utförare",
    nextPath: "/hitta-hemtjanst",
    datePublished: "2026-07-12",
    dateModified: "2026-07-25",
    faqs: [
      {
        question: "Kan jag byta hemtjänstutförare om jag inte är nöjd?",
        answer:
          "Ja. Du har alltid rätt att byta utförare. Byten sköts genom Stockholms stad, och den nya utföraren kan oftast börja inom kort. Jämför gärna några alternativ innan du bestämmer dig.",
      },
      {
        question: "Kostar det något att byta utförare?",
        answer:
          "Nej, själva bytet är kostnadsfritt. Din avgift beror på din inkomst och de insatser du är beviljad – inte på vilken utförare du väljer.",
      },
    ],
  },
  "checklista-hemtjanstutforare": {
    title: "Checklista för hemtjänstutförare",
    description:
      "Frågor att ta med när du jämför hemtjänstutförare i Stockholm för dig själv eller en närstående.",
    eyebrow: "Inför samtalet",
    keywords: [
      "checklista hemtjänstutförare",
      "frågor till hemtjänst",
      "jämföra hemtjänst Stockholm",
    ],
    intro:
      "Använd checklistan som stöd i samtal. Be om konkreta svar, skriv ned vem du talade med och skilj utförarens egna uppgifter från kommunens officiella beslut.",
    steps: [
      {
        title: "Start och tillgänglighet",
        body: "Fråga om enheten kan ta emot uppdraget på din adress, när den kan starta och hur den hanterar ändrade tider eller sena besök.",
      },
      {
        title: "Kontakt och kontinuitet",
        body: "Fråga vem som blir fast omsorgskontakt, hur ofta personalgruppen ändras och vem du eller en närstående kontaktar vid vardagliga frågor.",
      },
      {
        title: "Språk, bemötande och kompetens",
        body: "Beskriv vad som är viktigt i kommunikationen. Be enheten förklara hur önskat språk och särskild kompetens bemannas i praktiken.",
      },
      {
        title: "Planering och uppföljning",
        body: "Fråga hur genomförandeplanen tas fram, hur mål följs upp och hur synpunkter eller klagomål dokumenteras och återkopplas.",
      },
      {
        title: "Källor och egna påståenden",
        body: "Notera om svaret kommer från Stockholms stad, en oberoende kvalitetsmätning eller utförarens egen presentation. Saknade data ska leda till en fråga, inte en gissning.",
      },
    ],
    questions: [
      "Kan ni starta på min adress?",
      "Vem är min fasta kontakt?",
      "Hur meddelar ni förseningar?",
      "Hur säkras önskat språk?",
      "Hur följs genomförandeplanen upp?",
      "Vilka underleverantörer används?",
    ],
    officialLabel: "Kontrollera utföraren hos Stockholms stad",
    officialUrl: OFFICIAL_STOCKHOLM_CHOOSER_URL,
    nextLabel: "Spara tre alternativ",
    nextPath: "/hitta-hemtjanst",
    datePublished: "2026-07-12",
    dateModified: "2026-07-12",
  },
};

const GUIDE_INDEX_STEPS = [
  "Du ansöker om hemtjänst hos Stockholms stad.",
  "En biståndshandläggare utreder dina behov, och du får ett skriftligt beslut om vilken hjälp du beviljas.",
  "Du väljer själv mellan kommunala och privata utförare. Väljer du ingen får du en utförare anvisad.",
  "Avgiften beror på din inkomst och är aldrig högre än maxtaxan, 2 660 kronor i månaden 2026.",
  "Är du inte nöjd har du rätt att byta utförare. Bytet görs via Stockholms stad.",
] as const;

export function GuideIndexPage() {
  const canonicalUrl = `${SITE_ORIGIN}/guider`;
  const trackStockholmEvent = useStockholmEventTracking();
  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title="Guider: ansöka, välja och byta hemtjänst i Stockholm"
        description="Praktiska guider för att ansöka om, välja, jämföra eller byta hemtjänst i Stockholms stad."
        canonicalUrl={canonicalUrl}
        keywords={[
          "guide hemtjänst Stockholm",
          "välja hemtjänst Stockholm",
          "ansöka hemtjänst Stockholm",
        ]}
        contentClassification="Consumer guide index"
        targetAudience="seniors, relatives, Stockholm residents"
        schemaMarkup={generateBreadcrumbSchema([
          { name: "Hem", url: SITE_ORIGIN },
          { name: "Guider", url: canonicalUrl },
        ])}
      />
      <header className="border-b border-border bg-accent/65">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-secondary">
            <BookOpen className="h-4 w-4" aria-hidden="true" /> Kunskap inför
            ditt val
          </p>
          <h1 className="mt-3 font-[var(--font-editorial)] text-4xl font-bold leading-tight text-foreground md:text-5xl">
            Guider om hemtjänst i Stockholm
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">
            Börja där du befinner dig: från vad hemtjänst är och vad det kostar
            till att välja, jämföra eller byta utförare. Vi länkar vidare till
            Stockholms stad när det är staden som bestämmer.
          </p>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2">
          {STOCKHOLM_GUIDES.map((guide, index) => (
            <Link
              key={guide.slug}
              to={`/guider/${guide.slug}`}
              onClick={() =>
                trackStockholmEvent("seo_funnel_click", {
                  source_surface: "guide",
                  target_path: `/guider/${guide.slug}`,
                })
              }
              className="group rounded-2xl border border-border bg-card p-6 shadow-sm transition hover:-translate-y-1 hover:border-brand-primary/35 hover:shadow-lg motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-primary text-lg font-bold text-white">
                {index + 1}
              </span>
              <h2 className="mt-5 text-2xl font-bold text-foreground">
                {guide.title}
              </h2>
              <p className="mt-3 leading-7 text-muted-foreground">
                {guide.description}
              </p>
              <span className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-brand-primary">
                Läs guiden
                <ArrowRight
                  className="h-4 w-4 transition group-hover:translate-x-1 motion-reduce:transform-none"
                  aria-hidden="true"
                />
              </span>
            </Link>
          ))}
        </div>
        <section
          className="mt-12 rounded-2xl border border-border bg-card p-6 md:p-8"
          aria-labelledby="guide-steps-heading"
        >
          <h2
            id="guide-steps-heading"
            className="text-2xl font-bold text-foreground"
          >
            Så går det till – i korthet
          </h2>
          <ol className="mt-5 list-decimal space-y-3 pl-6 text-lg leading-8 text-muted-foreground">
            {GUIDE_INDEX_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
        <ConversionCtaBand sourceSurface="guide" />
      </main>
    </>
  );
}

export default function GuidePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const guide = GUIDES[slug];
  const { hrefFor } = useJourney();
  const trackStockholmEvent = useStockholmEventTracking();
  if (!guide) {
    return (
      <NotFoundPage
        title="Guiden hittades inte"
        description="Den här guiden finns inte."
        navigationLinks={[
          { label: "Startsidan", to: "/" },
          { label: "Hitta hemtjänst", to: "/hitta-hemtjanst" },
        ]}
      />
    );
  }

  const canonicalUrl = `${SITE_ORIGIN}/guider/${slug}`;
  const schema: object[] = [
    generateArticleSchema({
      headline: guide.title,
      description: guide.description,
      url: canonicalUrl,
      datePublished: guide.datePublished,
      dateModified: guide.dateModified,
      keywords: guide.keywords,
    }),
    generateBreadcrumbSchema([
      { name: "Hem", url: SITE_ORIGIN },
      { name: "Guider", url: `${SITE_ORIGIN}/guider` },
      { name: guide.title, url: canonicalUrl },
    ]),
  ];
  if (guide.faqs?.length) {
    schema.push(generateFAQSchema(guide.faqs, canonicalUrl));
  }

  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title={fitTitle([
          `${guide.title} | Stockholms Hemtjänstguide`,
          guide.title,
        ])}
        description={guide.description}
        canonicalUrl={canonicalUrl}
        keywords={guide.keywords}
        ogType="article"
        contentClassification="Consumer guide"
        targetAudience="seniors, relatives, Stockholm residents"
        schemaMarkup={schema}
      />
      <article>
        <header className="border-b border-border bg-accent/65">
          <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
            <nav
              aria-label="Brödsmulor"
              className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
            >
              <Link to="/" className="hover:text-brand-primary hover:underline">
                Hem
              </Link>
              <span aria-hidden="true">/</span>
              <Link
                to="/guider"
                className="hover:text-brand-primary hover:underline"
              >
                Guider
              </Link>
              <span aria-hidden="true">/</span>
              <span className="text-foreground">{guide.title}</span>
            </nav>
            <p className="text-sm font-bold uppercase tracking-wider text-brand-secondary">
              {guide.eyebrow}
            </p>
            <h1 className="mt-3 font-[var(--font-editorial)] text-4xl font-bold leading-tight text-foreground md:text-5xl">
              {guide.title}
            </h1>
            <p className="mt-6 text-xl leading-9 text-muted-foreground">
              {guide.intro}
            </p>
          </div>
        </header>

        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          <ol className="space-y-6">
            {guide.steps.map((step, index) => (
              <li
                key={step.title}
                className="grid gap-4 rounded-2xl border border-border bg-card p-6 shadow-sm sm:grid-cols-[3rem_1fr]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-primary text-lg font-bold text-white">
                  {index + 1}
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    {step.title}
                  </h2>
                  <p className="mt-3 text-lg leading-8 text-muted-foreground">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <section
            className="mt-10 rounded-2xl border border-brand-secondary/25 bg-brand-secondary/5 p-6"
            aria-labelledby="questions-heading"
          >
            <h2
              id="questions-heading"
              className="text-2xl font-bold text-foreground"
            >
              Frågor att ta med
            </h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {guide.questions.map((question) => (
                <li
                  key={question}
                  className="flex gap-3 leading-7 text-muted-foreground"
                >
                  <CheckCircle2
                    className="mt-1 h-5 w-5 shrink-0 text-brand-primary"
                    aria-hidden="true"
                  />{" "}
                  {question}
                </li>
              ))}
            </ul>
          </section>

          {guide.faqs?.length ? (
            <section className="mt-10" aria-labelledby="faq-heading">
              <h2
                id="faq-heading"
                className="text-2xl font-bold text-foreground"
              >
                Vanliga frågor
              </h2>
              <div className="mt-5 space-y-3">
                {guide.faqs.map((faq) => (
                  <details
                    key={faq.question}
                    className="group rounded-2xl border border-border bg-card p-5 shadow-sm"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold text-foreground">
                      {faq.question}
                      <ChevronDown
                        className="h-5 w-5 shrink-0 text-brand-primary transition group-open:rotate-180 motion-reduce:transition-none"
                        aria-hidden="true"
                      />
                    </summary>
                    <p className="mt-3 text-lg leading-8 text-muted-foreground">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          ) : null}

          <aside className="mt-8 flex gap-3 rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-5 text-sm leading-6 text-muted-foreground">
            <Info
              className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary"
              aria-hidden="true"
            />
            <p>
              Guiden är generell vägledning. Ditt beslut och Stockholms stads
              aktuella information gäller alltid före vår sammanfattning.
            </p>
          </aside>

          <div className="mt-10 flex flex-col gap-4 rounded-3xl bg-brand-primary p-7 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold">Nästa steg</h2>
              <p className="mt-2 text-white/80">
                Fortsätt med aktuella källor och en kort jämförelse.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <Link
                to={hrefFor(guide.nextPath)}
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "guide",
                    target_path: guide.nextPath.split("?")[0] || guide.nextPath,
                  })
                }
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {guide.nextLabel}{" "}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href={guide.officialUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "guide",
                    target_path: "/outbound/stockholm-official",
                  })
                }
                className="inline-flex min-h-11 items-center justify-center gap-2 text-sm font-semibold text-white underline underline-offset-4"
              >
                {guide.officialLabel}{" "}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
          <ConversionCtaBand sourceSurface="guide" />
        </div>
      </article>
    </>
  );
}
